import { useNavigate, useParams } from 'react-router-dom'

import { useQuery } from '@apollo/client'
import { Section } from '@factorialco/deprecated-design-system'
import { ApprovePromotionPageDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import AuthorizeUser from 'modules/core/components/AuthorizeUser'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import { employeeLegalEntityId } from 'modules/core/lib/employeeLegalEntity'
import i18n from 'modules/core/lib/i18n'
import { employeeUser } from 'modules/core/store/collections/users'
import type User from 'modules/core/store/models/User'

import { FLOWS } from 'modules/contracts/components/FlowSelectorModal/constants'
import useFetchCompensationsWithContractStatus from 'modules/contracts/hooks/useFetchCompensationsWithStatus'
import useFetchLegalEntityContractData from 'modules/contracts/hooks/useFetchLegalEntityContractData'
import { ContractHistoryDataProvider } from 'modules/contracts/lib/contractHistoryDataContext'
import { ContractVersionsProvider } from 'modules/contracts/lib/contractVersionsContext'
import { ApprovalStatus } from 'modules/contracts/lib/enums'
import { LegalEntityContractDataProvider } from 'modules/contracts/lib/legalEntityContractDataContext'
import { employeeContractVersions } from 'modules/contracts/lib/utils'
import contractVersions from 'modules/contracts/store/collections/contractVersions'
import employees from 'modules/employees/store/collections/employees'
import Page from 'modules/layout/components/Page'
import Header from 'modules/layout/components/Page/Header'

import Content from './_Content'
import useApprovePage from './hooks/useApprovePage'

const ApprovePromotionPage = () => {
  const params = useParams()
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const employeeId = Number(params.id)
  const employee = employees.get(employeeId)!
  const requestedContractVersionId = Number(params.contractVersionId)

  const contractVersionIds = employeeContractVersions(contractVersions, employee)
    .map((cv) => cv.id)
    .concat([requestedContractVersionId])

  const backPath = navigation.employees.id.contractVersions.fullPath({
    id: employeeId,
  })

  const {
    loading: isApprovePageDataLoading,
    canSeePage,
    contractVersionRequest,
    requester,
  } = useApprovePage(employee, requestedContractVersionId)

  const rejectRequest = async (reason?: string) => {
    const { success } = await contractVersionRequest!.reject(reason)
    if (success) {
      track(trackingEvents.employee.contracts.rejectContractRequest, {
        request_type: FLOWS.promotion,
        request_date: contractVersionRequest!.attributes.approval_request_created_at,
        request_reason: contractVersionRequest!.attributes.request_details,
      })
      navigate(backPath)
    }
  }

  const approveRequest = async () => {
    const { success } = await contractVersionRequest!.approve()
    if (success) {
      track(trackingEvents.employee.contracts.approveContractRequest, {
        request_type: FLOWS.promotion,
        request_date: contractVersionRequest!.attributes.approval_request_created_at,
        request_reason: contractVersionRequest!.attributes.request_details,
      })
      navigate(backPath)
    }
  }

  const deleteRequest = async () => {
    const { success } = await asyncAction(() => contractVersionRequest!.delete())
    if (success) {
      navigate(backPath)
    }
  }

  const getHeaderTitle = (requester: User | undefined, actionType: string | undefined) => {
    if (actionType === FLOWS.promotion) {
      return requester
        ? i18n.t('contract_versions.approval_page.promotion.title', {
            requesterName: requester.fullName(),
            employeeName: employeeUser(employee).fullName(),
          })
        : i18n.t('contract_versions.approval_page.promotion.loading_title', {
            employeeName: employeeUser(employee).fullName(),
          })
    }

    if (actionType === FLOWS.evolution) {
      return requester
        ? i18n.t('contract_versions.approval_page.evolution.title', {
            requesterName: requester.fullName(),
            employeeName: employeeUser(employee).fullName(),
          })
        : i18n.t('contract_versions.approval_page.evolution.loading_title', {
            employeeName: employeeUser(employee).fullName(),
          })
    }

    return ''
  }

  const {
    loading: isLegalEntityDataLoading,
    spanishContractData,
    frenchContractData,
    germanContractData,
    portugueseContractData,
    commonContractData,
  } = useFetchLegalEntityContractData(
    employeeLegalEntityId(employee),
    contractVersionRequest?.attributes.country,
    true
  )
  const additionalCompensationsData = useFetchCompensationsWithContractStatus(contractVersionIds, [
    ApprovalStatus.pending,
    ApprovalStatus.approved,
    ApprovalStatus.null,
  ])

  const newAdditionalCompensationsResult = useQuery(ApprovePromotionPageDocument, {
    variables: {
      contractVersionIds,
    },
  })

  const newAdditionalCompensations =
    newAdditionalCompensationsResult?.data?.contracts.additionalCompensationsConnection.nodes ?? []

  const isLoading =
    isApprovePageDataLoading || additionalCompensationsData.loading || isLegalEntityDataLoading
  const isNotFound = !canSeePage
  const headerTitle = getHeaderTitle(
    requester,
    contractVersionRequest?.attributes.approval_action_type
  )

  return (
    <AuthorizeUser authorizeFn={() => canSeePage || isLoading}>
      <Page
        isLoading={isLoading}
        notFound={{
          active: isNotFound && !isLoading,
          title: i18n.t('errors.page_not_found.title'),
          description: i18n.t('errors.page_not_found.description'),
        }}
      >
        <Page.Header>
          <Header.Title backTo={backPath}>{headerTitle}</Header.Title>
        </Page.Header>
        <Page.Body>
          <Section.Empty>
            <LegalEntityContractDataProvider
              spanishContractData={spanishContractData}
              frenchContractData={frenchContractData}
              germanContractData={germanContractData}
              portugueseContractData={portugueseContractData}
              commonContractData={commonContractData}
            >
              <ContractHistoryDataProvider
                additionalCompensations={additionalCompensationsData}
                newAdditionalCompensations={newAdditionalCompensations}
              >
                <ContractVersionsProvider collection={contractVersions}>
                  {contractVersionRequest && (
                    <Content
                      contractVersionRequest={contractVersionRequest!}
                      rejectRequest={rejectRequest}
                      approveRequest={approveRequest}
                      deleteRequest={deleteRequest}
                    />
                  )}
                </ContractVersionsProvider>
              </ContractHistoryDataProvider>
            </LegalEntityContractDataProvider>
          </Section.Empty>
        </Page.Body>
      </Page>
    </AuthorizeUser>
  )
}

export default observer(ApprovePromotionPage)
